import React, { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import SelectList from "../../../shared/inputs/SelectList";
import { useTogal } from "./TogalContext";
import { fetchTogalProjects, fetchTogalSets } from "../../../../integrations/qto/sources/togal/api";
import type { TogalProject, TogalSet } from "../../../../integrations/qto/sources/togal/types";
import { useBanner } from "../../../context/BannerContext";
import { workbookCache } from "../../../../excel/workbook/cache";
import TogalLoginForm from "./TogalLoginForm";
import { TOGAL_LOGIN_USERNAME_KEY } from "../../../../integrations/qto/sources/togal/api";
import TakeOffSourceHeader from "../shared/TakeOffSourceHeader";
import AutocompleteField from "../../../shared/inputs/AutocompleteField";
import { useConnectionTransition } from "../../../hooks/useConnectionTransition";
import { useSignOutReset } from "../../../hooks/useSignOutReset";
import { useDrawerOpenReset } from "../../../hooks/useDrawerOpenReset";
import {
  DRAWING_SET_HEADING,
  TOGAL_MISSING_ORG,
  TOGAL_PROJECTS_LOAD_ERROR,
  TOGAL_SETS_EMPTY,
  TOGAL_SETS_LOAD_ERROR,
} from "../../../constants";
import { ediphiPalette } from "../../../styles/colors";
import { browserStore } from "../../../../utils/storageKeys";

const TogalList: React.FC = () => {
  const {
    togalState,
    togalProject,
    togalSet,
    isTogalAuthenticated,
    togalUserProfile,
    connectTogalFromIds,
    selectorPrefillProject,
    clearSelectorPrefill,
  } = useTogal();
  const { showBanner } = useBanner();

  const [project, setProject] = useState<TogalProject | null>(null);
  const [projects, setProjects] = useState<TogalProject[]>([]);
  const [sets, setSets] = useState<TogalSet[]>([]);
  const [loadingProjects, setLoadingProjects] = useState<boolean>(true);
  const [loadingSets, setLoadingSets] = useState<boolean>(false);
  const [projectsLoaded, setProjectsLoaded] = useState<boolean>(false);
  const [prefillDone, setPrefillDone] = useState<boolean>(false);

  useConnectionTransition("togal", togalState, () => setProject(null));

  // Shared by both triggers so a reopen and a sign-out cannot reset different amounts.
  const resetSelection = useCallback(() => {
    setPrefillDone(false);
    setProject(null);
  }, []);

  useDrawerOpenReset("takeOff", resetSelection);
  useSignOutReset(isTogalAuthenticated, resetSelection);

  useEffect(() => {
    if (!isTogalAuthenticated) return;

    let isCurrent = true;
    const load = async () => {
      setLoadingProjects(true);
      const result = await fetchTogalProjects();
      if (!isCurrent) return;
      if (result.success && result.data) {
        setProjects(result.data.filter((p) => p.active));
      } else {
        setProjects([]);
        showBanner(TOGAL_PROJECTS_LOAD_ERROR, "error");
      }
      setProjectsLoaded(true);
      setLoadingProjects(false);
    };

    void load();
    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTogalAuthenticated]);

  // Runs once per drawer open, gated on `projectsLoaded` rather than on `project` being empty —
  // the user can empty it themselves with the field's clear button, and re-filling it then would
  // make the field impossible to clear.
  //
  // Prefill wins over the connected project so "change project" from a connected card still
  // lands where the user asked; otherwise edit mode reopens on the current selection.
  useEffect(() => {
    if (!projectsLoaded || prefillDone) return;
    setPrefillDone(true);
    if (selectorPrefillProject) {
      setProject(selectorPrefillProject);
      clearSelectorPrefill();
      return;
    }
    // Membership-checked: a connected project since deactivated is filtered out of `projects`,
    // and selecting a value absent from the options makes MUI warn.
    if (togalProject && projects.some((p) => p.id === togalProject.id)) setProject(togalProject);
  }, [projectsLoaded, prefillDone, selectorPrefillProject, clearSelectorPrefill, togalProject, projects]);

  useEffect(() => {
    if (!project || !togalUserProfile?.organization_id) {
      setSets([]);
      return;
    }

    let isCurrent = true;
    const load = async () => {
      setLoadingSets(true);
      const result = await fetchTogalSets(project.id, project.organization_id || togalUserProfile.organization_id);
      if (!isCurrent) return;
      if (result.success && result.data) {
        setSets(result.data.rows);
      } else {
        // Cleared, not left in place: these are the previous project's sets, and once loading drops
        // they are clickable again — connecting one would pair it with the project now selected.
        setSets([]);
        showBanner(TOGAL_SETS_LOAD_ERROR, "error");
      }
      setLoadingSets(false);
    };

    void load();
    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, togalUserProfile?.organization_id]);

  const handleSelectSet = async (set: TogalSet) => {
    if (!project) return;

    const orgId = togalUserProfile?.organization_id || project.organization_id || "";
    if (!orgId) {
      showBanner(TOGAL_MISSING_ORG, "error");
      return;
    }

    browserStore.set("togalOrganizationId", orgId);
    await workbookCache.set({ togalProjectId: project.id, togalSetId: set.id });

    // No close here: useConnectionTransition closes on the connected transition, so a failed connect
    // leaves the drawer open on this list for an immediate retry.
    await connectTogalFromIds(project, set);
  };

  if (!isTogalAuthenticated) return <TogalLoginForm />;

  return (
    <Box>
      <Box sx={{ p: 2 }}>
        <Box sx={{ mb: "16px" }}>
          <TakeOffSourceHeader type="togal" email={browserStore.get(TOGAL_LOGIN_USERNAME_KEY) ?? undefined} />
        </Box>
        <AutocompleteField<TogalProject>
          label="Project"
          options={projects}
          value={project}
          onChange={setProject}
          getOptionLabel={(option) => option.name}
          getOptionId={(option) => option.id}
          loading={loadingProjects}
        />
      </Box>
      {project && (
        <Box
          sx={{
            p: 2,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            borderTop: `1px solid ${ediphiPalette.borderDivider}`,
          }}
        >
          <SelectList<TogalSet>
            title={DRAWING_SET_HEADING}
            items={sets.map((s) => ({ id: s.id, label: s.name, data: s }))}
            onSelectItem={(set) => void handleSelectSet(set)}
            selectedItemId={togalSet?.id ?? null}
            // Covers connecting as well as fetching: the rows are the only click target, and the
            // drawer now stays open while the connect runs.
            loading={loadingSets || togalState === "loading"}
            emptyMessage={TOGAL_SETS_EMPTY}
          />
        </Box>
      )}
    </Box>
  );
};

export default TogalList;
